import {
  BookOpenText,
  ChartBar,
  Ear,
  Microphone,
  PencilLine,
  Translate,
  Books,
  TextAa,
  type Icon,
} from '@phosphor-icons/react';

import { HeaderContent, HEADER_TITLE_CLASS } from '../../../components/shell/app-header';
import { Link, useSearchParams } from '../../../lib/routing';
import { OUTILS_ENSEIGNANT, DOMAINES_AVEC_PARCOURS } from './parcours-config';
import { BILAN, DOMAINES, NIVEAUX, niveauDepuisUrl, type DomaineId } from './accueil-config';

const ICONES: Record<DomaineId, Icon> = {
  fluence: Ear,
  comprehension: BookOpenText,
  vocabulaire: Translate,
  grammaire: TextAa,
  ecriture: PencilLine,
  oral: Microphone,
  culture: Books,
};

const CLASSE_ENTREE = 'min-h-[44px] rounded-sm border border-border bg-card px-4 py-3 flex items-center gap-3';
const CLASSE_TITRE = 'flex-1 font-semibold text-[color:var(--naia-text-primary)]';
const CLASSE_ETIQUETTE = 'text-[13px] text-[color:var(--naia-text-secondary)]';
const CLASSE_NIVEAU = 'min-h-[44px] min-w-[44px] rounded-sm border border-border px-4 font-semibold';

export default function FrancaisCollegeApp() {
  const [params, setParams] = useSearchParams();
  const niveau = niveauDepuisUrl(params.get('niveau'));

  const choisir = (id: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('niveau', id);
        return next;
      },
      { replace: true },
    );

  return (
    <>
      <HeaderContent width="md" showBurger backTo="/mini-apps" backLabel="Mini-apps">
        <div className="min-w-0">
          <h1 className={HEADER_TITLE_CLASS}>Français collège — prototype</h1>
          <p className="m-0 mt-0.5 text-[13px] leading-snug text-[color:var(--naia-text-secondary)]">
            Prototype en construction, contenus en brouillon à valider.
          </p>
        </div>
      </HeaderContent>
      <div role="group" aria-label="Niveau" className="flex flex-wrap gap-2 px-4 pt-4">
        {NIVEAUX.map(({ id, libelle, actif }) => (
          <button
            key={id}
            type="button"
            aria-pressed={actif ? id === niveau : undefined}
            aria-disabled={actif ? undefined : true}
            onClick={actif ? () => choisir(id) : undefined}
            className={`${CLASSE_NIVEAU} ${
              id === niveau
                ? 'bg-primary text-primary-foreground'
                : 'bg-card text-[color:var(--naia-text-primary)]'
            } ${actif ? '' : 'cursor-not-allowed opacity-70'}`}
          >
            {libelle}
            {actif ? null : <span className={`block ${CLASSE_ETIQUETTE}`}>À venir</span>}
          </button>
        ))}
      </div>
      <ul className="m-0 list-none p-4 flex flex-col gap-3" aria-label="Domaines">
        {DOMAINES.map(({ id, titre }) => {
          const Icone = ICONES[id];
          const to = DOMAINES_AVEC_PARCOURS.includes(id) ? `/mini-apps/francais-college/domaine/${id}` : undefined;
          return (
            <li key={id}>
              {to ? (
                <Link to={to} className={`${CLASSE_ENTREE} no-underline`}>
                  <Icone size={24} aria-hidden />
                  <span className={CLASSE_TITRE}>{titre}</span>
                  <span className={CLASSE_ETIQUETTE}>Brouillon</span>
                </Link>
              ) : (
                <div className={CLASSE_ENTREE}>
                  <Icone size={24} aria-hidden />
                  <span className={CLASSE_TITRE}>{titre}</span>
                  <span className={CLASSE_ETIQUETTE}>À venir</span>
                </div>
              )}
            </li>
          );
        })}
        <li>
          <Link to={BILAN.to} className={`${CLASSE_ENTREE} no-underline`}>
            <ChartBar size={24} aria-hidden />
            <span className={CLASSE_TITRE}>{BILAN.titre}</span>
            <span className={CLASSE_ETIQUETTE}>Brouillon</span>
          </Link>
        </li>
      </ul>
      <section aria-labelledby="outils-enseignant" className="px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
        <h2 id="outils-enseignant" className="m-0 mb-1 text-base font-semibold text-[color:var(--naia-text-primary)]">
          Outils enseignant
        </h2>
        <p className="m-0 mb-3 text-[13px] leading-snug text-[color:var(--naia-text-secondary)]">
          Relectures de contenu, réservées aux enseignants — pas du contenu élève.
        </p>
        <ul className="m-0 list-none p-0 flex flex-col gap-3">
          {OUTILS_ENSEIGNANT.map(({ slug, titre }) => (
            <li key={slug}>
              <Link to={`/mini-apps/francais-college/parcours/${slug}`} className={`${CLASSE_ENTREE} no-underline`}>
                <BookOpenText size={24} aria-hidden />
                <span className={CLASSE_TITRE}>{titre}</span>
                <span className={CLASSE_ETIQUETTE}>Enseignant</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
